import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChatMessage } from '@/components/chat/ChatMessage'
import { useChat } from '@/hooks/useChat'
import { useSession } from '@/hooks/useSession'
import { useAppearance } from '@/hooks/useAppearance'
import { useAuth } from '@/hooks/useAuth'
import { HelixWordmark, Avatar } from '@/components/ui/Logo'
import { Icon, type IconName } from '@/components/ui/Icon'
import { SignInModal } from '@/components/auth/SignInModal'
import { CSATModal } from '@/components/csat/CSATModal'
import { VoiceToTextModal } from '@/components/voice/VoiceToTextModal'
import {
  isRtl as localeIsRtl, changeLocale, hasManualLocale, detectLang,
  SUPPORTED_LOCALES, LOCALE_LABELS,
} from '@/lib/i18n'
import { getCurrentOrgId } from '@/lib/org'
import type { Message } from '@/types/message'
import type { Session } from '@/types/session'

interface ChatScreenProps { embedded?: boolean }
interface Suggestion { icon: IconName; label: string; hue: string; requiresAuth?: boolean }

const ANON_META: { icon: IconName; hue: string; requiresAuth?: boolean }[] = [
  { icon: 'globe', hue: '#2767FF' },
  { icon: 'fileText', hue: '#2767FF' },
  { icon: 'refresh', hue: '#2767FF' },
  { icon: 'user', hue: '#2767FF' },
  { icon: 'user', hue: '#2767FF', requiresAuth: true },
  { icon: 'bolt', hue: '#2767FF', requiresAuth: true },
]
const AUTH_META: { icon: IconName; hue: string }[] = [
  { icon: 'user', hue: '#2767FF' },
  { icon: 'bolt', hue: '#2767FF' },
  { icon: 'fileText', hue: '#2767FF' },
  { icon: 'shield', hue: '#2767FF' },
]
const AR_FONT = "'Noto Sans Arabic','Noto Naskh Arabic','DM Sans',sans-serif"

function fallbackWelcome(activeSessionId: string | null, welcome: string): Message {
  return { id: '__welcome__', sessionId: activeSessionId ?? '', role: 'assistant', content: welcome, createdAt: new Date().toISOString() }
}

function relTime(session: Session): string {
  const d = new Date(session.updatedAt)
  if (Number.isNaN(d.getTime())) return 'Now'
  const days = Math.round((d.getTime() - Date.now()) / 86_400_000)
  if (days === 0) return new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' }).format(d)
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(days, 'day')
}

export function ChatScreen({ embedded = false }: ChatScreenProps) {
  const navigate = useNavigate()
  const threadRef = useRef<HTMLDivElement>(null)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const [draft, setDraft] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(() => !embedded && (typeof window === 'undefined' || window.innerWidth > 860))
  const [dark, setDark] = useState(() => (typeof window === 'undefined' ? false : localStorage.getItem('helix-theme') === 'dark'))
  const toggleDark = () => setDark((value) => { localStorage.setItem('helix-theme', value ? 'light' : 'dark'); return !value })
  const [authOpen, setAuthOpen] = useState(false)
  const [voiceTextOpen, setVoiceTextOpen] = useState(false)
  const [csatSessionId, setCsatSessionId] = useState<string | null>(null)
  const [atBottom, setAtBottom] = useState(true)
  const [composerFocus, setComposerFocus] = useState(false)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 860)

  const { messages, isTyping, sendMessage, handleAction, handleReact, loadHistory, refreshMessages } = useChat()
  const { sessions, activeSessionId, isLoading, fetchSessions, createNewSession, restoreAnonymousSession, selectSession, deleteSession } = useSession()
  const { config, voiceEnabled, welcomeMessage, orgName, logoUrl, agentAvatarUrl } = useAppearance()
  const { isAuthenticated, user, signOut } = useAuth()
  const { t, i18n } = useTranslation()
  const currentOrgId = getCurrentOrgId()
  const voicePath = '/voice'
  const activeSession = sessions.find((session) => session.id === activeSessionId)
  const lang = i18n.language || 'en'
  const rtl = localeIsRtl(lang)
  const setLang = (locale: string) => { void changeLocale(locale, true) }

  const cfg = config as {
    primaryColor?: string; accentColor?: string; fontFamily?: string; backgroundColor?: string
    textColor?: string; chatBubbleStyle?: string; darkModeEnabled?: boolean; defaultLocale?: string; suggestions?: string[]
  } | null

  useEffect(() => {
    if (!hasManualLocale() && cfg?.defaultLocale && SUPPORTED_LOCALES.includes(cfg.defaultLocale as 'en' | 'fr' | 'ar') && cfg.defaultLocale !== lang) {
      void changeLocale(cfg.defaultLocale, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.defaultLocale])

  const brandVars = useMemo(() => {
    const vars: Record<string, string> = {}
    if (cfg?.primaryColor && cfg.primaryColor !== '#3b82f6') {
      vars['--brand-blue'] = cfg.primaryColor
      vars['--brand-violet'] = cfg.primaryColor
      vars['--grad-cx'] = `linear-gradient(135deg, ${cfg.primaryColor}, ${cfg.accentColor || cfg.primaryColor})`
      vars['--grad-helix'] = vars['--grad-cx']
    }
    if (rtl) vars['--font-sans'] = AR_FONT
    else if (cfg?.fontFamily && cfg.fontFamily !== 'Inter') {
      const family = cfg.fontFamily.includes(',') || cfg.fontFamily.includes("'") ? cfg.fontFamily : `'${cfg.fontFamily}'`
      vars['--font-sans'] = `${family}, 'DM Sans', ui-sans-serif, system-ui, sans-serif`
    }
    if (!dark) {
      if (cfg?.backgroundColor && cfg.backgroundColor !== '#FFFFFF') vars['--bg'] = cfg.backgroundColor
      if (cfg?.textColor && cfg.textColor !== '#111827') vars['--ink'] = cfg.textColor
    }
    return vars
  }, [cfg, dark, rtl])

  useEffect(() => {
    if (localStorage.getItem('helix-theme') == null && cfg?.darkModeEnabled !== undefined) setDark(Boolean(cfg.darkModeEnabled))
  }, [cfg?.darkModeEnabled])
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 860)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  useEffect(() => {
    const run = async () => {
      if (isAuthenticated) await fetchSessions()
      const activeSessionMissing = Boolean(activeSessionId && sessions.length > 0 && !activeSession)
      if (!activeSessionId && !isAuthenticated && await restoreAnonymousSession()) return
      if (!activeSessionId || activeSessionMissing || (activeSession && activeSession.orgId !== currentOrgId)) await createNewSession()
    }
    void run().catch(() => undefined)
  }, [activeSession, activeSessionId, createNewSession, restoreAnonymousSession, currentOrgId, fetchSessions, isAuthenticated, sessions.length])
  useEffect(() => { if (activeSessionId) void Promise.resolve(loadHistory(activeSessionId)).catch(() => undefined) }, [activeSessionId, loadHistory])
  useEffect(() => {
    if (!activeSessionId) return
    const poll = setInterval(async () => {
      const state = await refreshMessages(activeSessionId)
      if (state?.closed && state.requestFeedback) setCsatSessionId((current) => current ?? activeSessionId)
    }, 4000)
    return () => clearInterval(poll)
  }, [activeSessionId, refreshMessages])
  useEffect(() => {
    const el = threadRef.current
    if (el && atBottom) el.scrollTop = el.scrollHeight
  }, [messages, isTyping, atBottom])
  useEffect(() => {
    const el = taRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(160, el.scrollHeight)}px`
  }, [draft])

  const visibleMessages = useMemo(() => messages.length > 0 ? messages : [fallbackWelcome(activeSessionId, welcomeMessage)], [activeSessionId, messages, welcomeMessage])
  const isEmpty = visibleMessages.length === 1 && visibleMessages[0].id === '__welcome__'
  const suggestions = useMemo<Suggestion[]>(() => {
    if (cfg?.suggestions && cfg.suggestions.length > 0) return cfg.suggestions.map((label, i) => ({ label, ...ANON_META[i % ANON_META.length] }))
    return isAuthenticated
      ? AUTH_META.map((meta, i) => ({ ...meta, label: t(`sug.auth.${i}`) }))
      : ANON_META.map((meta, i) => ({ ...meta, label: t(`sug.anon.${i}`) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg?.suggestions, isAuthenticated, lang])

  const canSend = Boolean(activeSessionId) && draft.trim().length > 0 && !isTyping
  const submit = async (content = draft) => {
    const text = content.trim()
    if (!text || isTyping || !activeSessionId) return
    if (!hasManualLocale()) {
      const detected = detectLang(text)
      if (detected && detected !== lang) void changeLocale(detected, false)
    }
    setDraft('')
    await sendMessage(text)
  }
  const chooseSuggestion = (suggestion: Suggestion) => {
    if (suggestion.requiresAuth && !isAuthenticated) { setAuthOpen(true); return }
    void submit(suggestion.label)
  }
  const startNewChat = async () => {
    if (activeSessionId && messages.length > 0) setCsatSessionId(activeSessionId)
    else await createNewSession()
  }
  const onThreadScroll = () => {
    const el = threadRef.current
    if (el) setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80)
  }

  return (
    <div dir={rtl ? 'rtl' : 'ltr'} className={`cx-app${dark ? ' helix-chat-dark' : ''}${sidebarOpen ? '' : ' sidebar-collapsed'}`} style={brandVars}>
      {!embedded && isMobile && sidebarOpen && <div className="helix-scrim" onClick={() => setSidebarOpen(false)} />}

      {!embedded && (
        <aside className={`cx-sidebar${isMobile ? ' mobile' : ''}${sidebarOpen ? ' open' : ''}`}>
          <div className="cx-sidebar-top">
            <HelixWordmark size={sidebarOpen || isMobile ? 48 : 38} compact={!sidebarOpen && !isMobile} />
            {(sidebarOpen || isMobile) && <button className="cx-icon-button" onClick={() => setSidebarOpen(false)} aria-label={t('sidebar.collapse')}><Icon name={rtl ? 'arrowRight' : 'arrowLeft'} size={17} /></button>}
          </div>
          {!sidebarOpen && !isMobile && <button className="cx-icon-button cx-expand" onClick={() => setSidebarOpen(true)} aria-label={t('sidebar.expand')}><Icon name={rtl ? 'arrowLeft' : 'arrowRight'} size={17} /></button>}

          <div className="cx-new-wrap">
            <button className="cx-new-button" onClick={() => void startNewChat()}>
              <Icon name="plus" size={18} />{(sidebarOpen || isMobile) && <span>{t('sidebar.newConversation')}</span>}
            </button>
          </div>

          {(sidebarOpen || isMobile) && (
            <>
              <div className="cx-recent-heading"><span>{t('sidebar.recent')}</span>{isLoading && <span>{t('sidebar.syncing')}</span>}</div>
              <div className="cx-session-list">
                {sessions.length === 0 && <div className="cx-no-sessions">{t('sidebar.noConversations')}</div>}
                {sessions.map((session) => {
                  const active = session.id === activeSessionId
                  return (
                    <button key={session.id} className={`cx-session${active ? ' active' : ''}`} onClick={() => void selectSession(session.id)}>
                      <span className="cx-session-icon"><Icon name="prompt" size={14} /></span>
                      <span className="cx-session-copy"><strong>{session.title || t('sidebar.untitled')}</strong><small>{session.lastMessage || `${session.status} conversation`}</small></span>
                      <time>{relTime(session)}</time>
                      <span role="button" tabIndex={0} className="cx-session-delete" onClick={(event) => { event.stopPropagation(); void deleteSession(session.id) }}><Icon name="trash" size={13} /></span>
                    </button>
                  )
                })}
              </div>

              <div className="cx-sidebar-status"><span className="cx-online-dot" /><span><strong>{orgName || 'Online agent'}</strong><small>{t('chat.online', { org: orgName || 'CX Agent' })}</small></span></div>
              <div className="cx-sidebar-account">
                <button className="cx-theme-button" onClick={toggleDark} aria-label={dark ? t('theme.light') : t('theme.dark')}><Icon name={dark ? 'sun' : 'moon'} size={15} /></button>
                {isAuthenticated && user ? (
                  <button className="cx-account-button" onClick={() => void signOut()}><Avatar name={user.displayName} size={32} gradient /><span><strong>{user.displayName}</strong><small>{t('auth.signOut')}</small></span><Icon name="arrowRight" size={16} /></button>
                ) : (
                  <button className="cx-account-button" onClick={() => setAuthOpen(true)}><span className="cx-account-icon"><Icon name="user" size={19} /></span><span><strong>{t('auth.signIn')}</strong><small>{t('sidebar.signInRest')}</small></span><Icon name={rtl ? 'arrowLeft' : 'arrowRight'} size={16} /></button>
                )}
              </div>
            </>
          )}
        </aside>
      )}

      <main className="cx-main">
        <header className="cx-topbar">
          {!embedded && isMobile && <button className="cx-icon-button" onClick={() => setSidebarOpen(true)}><Icon name="menu" size={18} /></button>}
          <div className="cx-topbar-spacer" />
          {logoUrl && <img src={logoUrl} alt={orgName} className="cx-org-logo" />}
          <div className="cx-language" role="group" aria-label="Language">
            {SUPPORTED_LOCALES.map((locale) => <button key={locale} className={lang === locale ? 'active' : ''} onClick={() => setLang(locale)}>{LOCALE_LABELS[locale]}</button>)}
          </div>
        </header>

        <div ref={threadRef} onScroll={onThreadScroll} className={`cx-thread${isEmpty ? ' empty' : ''}`}>
          {isEmpty ? (
            <section className="cx-hero">
              <div className="cx-hero-copy">
                <h1>{t(new Date().getHours() < 12 ? 'greeting.morning' : new Date().getHours() < 18 ? 'greeting.afternoon' : 'greeting.evening')}{isAuthenticated && user ? `, ${user.displayName.split(' ')[0]}` : ''}</h1>
                <h2>{t('hero.help')}</h2>
                <p>{welcomeMessage}</p>
              </div>

              {voiceEnabled ? (
                <button className="cx-voice-hero" onClick={() => navigate(voicePath)}>
                  <span className="cx-voice-center"><span className="cx-voice-mic"><Icon name="mic" size={34} /></span><strong>{t('composer.liveVoice')}</strong><small>{t('voice.listeningHint')}</small></span>
                </button>
              ) : null}

              <div className="cx-suggestions">
                {suggestions.slice(0, 4).map((suggestion, index) => (
                  <button key={`${suggestion.label}-${index}`} onClick={() => chooseSuggestion(suggestion)}>
                    <span><Icon name={suggestion.requiresAuth && !isAuthenticated ? 'lock' : suggestion.icon} size={19} /></span><strong>{suggestion.label}</strong>
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <div className="cx-message-column">
              <div className="cx-conversation-heading"><h1>{activeSession?.title || t('sidebar.newConversation')}</h1><span><i />{t('chat.online', { org: orgName })}</span></div>
              {visibleMessages.map((message) => <ChatMessage key={message.id} m={message} onReact={handleReact} onAction={handleAction} avatarUrl={agentAvatarUrl} t={t} />)}
            </div>
          )}
        </div>

        {!isEmpty && !atBottom && <button className="scroll-pill" onClick={() => { const el = threadRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); setAtBottom(true) }}><Icon name="arrowDown" size={13} /> {t('chat.latest')}</button>}

        <footer className="cx-composer-shell">
          <div className={`cx-composer${composerFocus ? ' focused' : ''}`}>
            <textarea ref={taRef} dir="auto" value={draft} rows={1} onChange={(event) => setDraft(event.target.value)} onFocus={() => setComposerFocus(true)} onBlur={() => setComposerFocus(false)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); void submit() } }} placeholder={isAuthenticated ? t('chat.placeholderAuth') : t('chat.placeholder')} aria-label="Message input" />
            <div className="cx-composer-controls">
              {voiceEnabled && <button className="cx-composer-icon" data-tip={t('composer.voiceInput')} onClick={() => setVoiceTextOpen(true)}><Icon name="mic" size={18} /></button>}
              {voiceEnabled && <button className="cx-composer-icon" data-tip={t('composer.liveVoice')} onClick={() => navigate(voicePath)}><Icon name="headphones" size={18} /></button>}
              <button className="cx-send" onClick={() => void submit()} disabled={!canSend} data-tip={t('composer.send')}><Icon name="send" size={18} /></button>
            </div>
          </div>
          <div className="cx-composer-hint"><span>{t('hero.disclaimer', { org: 'CX Agent' })}</span><span><kbd>↵</kbd> {t('chat.send')}</span></div>
        </footer>
      </main>

      <SignInModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      {csatSessionId && <CSATModal isOpen sessionId={csatSessionId} onClose={() => { setCsatSessionId(null); void createNewSession() }} />}
      <VoiceToTextModal isOpen={voiceTextOpen} onClose={() => setVoiceTextOpen(false)} onTranscript={(text) => { setVoiceTextOpen(false); if (text.trim()) void submit(text.trim()) }} />
    </div>
  )
}
